import { useMemo } from 'react';
import { useTripAssets, groupAssets, type TripAsset } from './useTripAssets';
import { calendarDaysBetween, localDateString } from '../lib/tripDates';

export type WarrantyStatus = 'ok' | 'expiring' | 'expired' | 'unknown';
export type FirmwareStatus = 'current' | 'update_available' | 'unknown';
export type LifecycleAction = 'none' | 'schedule_update' | 'renew' | 'act_now' | 'insufficient_data';

export interface LifecycleAsset extends TripAsset {
  warrantyStatus: WarrantyStatus;
  /** Days until warranty expiry (negative when expired); null when no date is recorded. */
  warrantyDays: number | null;
  firmwareStatus: FirmwareStatus;
  lifecycleAction: LifecycleAction;
}

export const WARRANTY_EXPIRING_DAYS = 90;

/**
 * Pure derivation from stored fields. A missing warranty date or a missing
 * "latest known" firmware is reported as unknown, never as healthy.
 */
export function toLifecycleAsset(asset: TripAsset, today: string = localDateString()): LifecycleAsset {
  const warrantyDays = asset.warranty_expiry ? calendarDaysBetween(today, asset.warranty_expiry) : null;
  const warrantyStatus: WarrantyStatus =
    warrantyDays === null
      ? 'unknown'
      : warrantyDays < 0
        ? 'expired'
        : warrantyDays < WARRANTY_EXPIRING_DAYS
          ? 'expiring'
          : 'ok';

  const firmwareStatus: FirmwareStatus =
    asset.firmware_latest_known && asset.firmware_version
      ? asset.firmware_latest_known === asset.firmware_version
        ? 'current'
        : 'update_available'
      : 'unknown';

  let lifecycleAction: LifecycleAction;
  if (warrantyStatus === 'expired' && firmwareStatus === 'update_available') lifecycleAction = 'act_now';
  else if (warrantyStatus === 'expired' || warrantyStatus === 'expiring') lifecycleAction = 'renew';
  else if (firmwareStatus === 'update_available') lifecycleAction = 'schedule_update';
  else if (warrantyStatus === 'unknown' || firmwareStatus === 'unknown') lifecycleAction = 'insufficient_data';
  else lifecycleAction = 'none';

  return { ...asset, warrantyStatus, warrantyDays, firmwareStatus, lifecycleAction };
}

export function useHardwareLifecycle(locationId: string | undefined) {
  const query = useTripAssets(locationId);
  const data = useMemo(() => {
    if (!query.data) return undefined;
    const today = localDateString();
    const lifecycle = query.data.assets.map((a) => toLifecycleAsset(a, today));
    const grouped = groupAssets(lifecycle);
    return {
      assets: lifecycle,
      solutionTypes: grouped.solutionTypes,
      byType: grouped.byType as Record<string, LifecycleAsset[]>,
    };
  }, [query.data]);
  return { ...query, data };
}
